import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ReactText } from 'react';
import { toast } from 'react-toastify';
import { stringResources } from 'src/assets/config/stringResources';
import { RootState } from 'src/redux/store';
import { ContractCePointRequest, ContractCePointResponse, ContractCePointsClient } from 'src/utility/apis/crewPayApiClient';
import axios from 'src/utility/apis/crewPayAxios';
import { handleError } from 'src/utility/utils';
import { CePointsDataModel } from 'src/views/contract/vesselContract/Import/DataTypes';

export interface ContractCePoint extends ContractCePointResponse { }

export interface State {
    isLoading: boolean;
    error: boolean;
    contractCePointList: ContractCePoint[];
}

const initialState: State = {
    isLoading: true,
    error: false,
    contractCePointList: [],
};

const contractCePointsSlice = createSlice({
    name: 'contractCePoints',
    initialState,
    reducers: {
        startLoading: (state) => {
            state.isLoading = true;
        },
        hasError: (state, action) => {
            state.error = action.payload;
            state.isLoading = false;
        },
        getContractCePointListSuccess: (state, action: PayloadAction<ContractCePoint[]>) => {
            state.contractCePointList = action.payload;
            state.isLoading = false;
        },
        addNewContractCePointSuccess: (state, action: PayloadAction<ContractCePoint>) => {
            const newItem = action.payload;
            state.contractCePointList = [newItem, ...state.contractCePointList];
            toast.success(stringResources.REDUX_SLICE.CREATE_SUCCESS);
            state.isLoading = false;
        },
        updateContractCePointSuccess: (state, action: PayloadAction<ContractCePoint>) => {
            const updatedItem = action.payload;
            const index = state.contractCePointList.findIndex(item => item.contractCePointId === updatedItem.contractCePointId);
            state.contractCePointList = [...state.contractCePointList.slice(0, index), updatedItem, ...state.contractCePointList.slice(index + 1)];
            toast.success(stringResources.REDUX_SLICE.UPDATE_SUCCESS);
            state.isLoading = false;
        },
        deleteContractCePointSuccess: (state, action: PayloadAction<number>) => {
            const contractCePointId = action.payload;
            state.contractCePointList = state.contractCePointList.filter(item => item.contractCePointId !== contractCePointId);
            toast.success(stringResources.REDUX_SLICE.DELETE_SUCCESS);
            state.isLoading = false;
        },
    },
});

// Reducer
export default contractCePointsSlice.reducer

// Actions
export const {
    startLoading,
    hasError,
} = contractCePointsSlice.actions;

// for useSelector
export const selectContractCePoints = (state: RootState) => state.contractCePoints as State;

// ----------------------------------------------------------------------

export function getContractCePointList(vesselContractId: number) {
    return async (dispatch, getState) => {
        dispatch(contractCePointsSlice.actions.startLoading());
        try {
            const client = new ContractCePointsClient(undefined, axios);
            const response = await client.getByVesselContractId(vesselContractId);
            dispatch(contractCePointsSlice.actions.getContractCePointListSuccess(response));
            return response;
        } catch (error) {
            dispatch(contractCePointsSlice.actions.hasError(true));
            handleError(error);
        }
    };
}

export function createContractCePoint(contractCePoint: ContractCePoint) {
    return async (dispatch, getState) => {
        dispatch(contractCePointsSlice.actions.startLoading());
        try {
            const { contractCePointId, ...payload } = contractCePoint;
            const request = payload as ContractCePointRequest;
            const client = new ContractCePointsClient(undefined, axios);
            const response = await client.create(request);
            dispatch(contractCePointsSlice.actions.addNewContractCePointSuccess(response));
        } catch (error) {
            dispatch(contractCePointsSlice.actions.hasError(true));
            handleError(error);
        }
    };
}

export function createContractCePointBatch(vesselContractId: number, CostsData: CePointsDataModel[],
    toastId: ReactText, currentProgress: number, totalProgress: number) {
    return async (dispatch, getState) => {
        try {
            const client = new ContractCePointsClient(undefined, axios);
            for (const d of CostsData) {
                const request = {
                    vesselContractId: vesselContractId,
                    minDays: d.MinDays,
                    maxDays: d.MaxDays,
                    points: d.Points
                } as ContractCePointRequest;
                await client.create(request);
                currentProgress += 1;
                toast.update(toastId, { render: `${stringResources.IMPORT.COSTS_IMPORTING} (${currentProgress}/${totalProgress})`, progress: currentProgress / totalProgress });
            }
        } catch (error) {
            handleError(error);
        }
    };
}

export function updateContractCePoint(contractCePoint: ContractCePoint) {
    return async (dispatch, getState) => {
        dispatch(contractCePointsSlice.actions.startLoading());
        try {
            const { contractCePointId, ...payload } = contractCePoint;
            const request = payload as ContractCePointRequest;
            const client = new ContractCePointsClient(undefined, axios);
            const response = await client.edit(contractCePointId, request);
            dispatch(contractCePointsSlice.actions.updateContractCePointSuccess(response));
        } catch (error) {
            dispatch(contractCePointsSlice.actions.hasError(true));
            handleError(error);
        }
    };
}

export function deleteContractCePoint(contractCePointId: number) {
    return async (dispatch, getState) => {
        dispatch(contractCePointsSlice.actions.startLoading());
        try {
            const client = new ContractCePointsClient(undefined, axios);
            await client.delete(contractCePointId);
            dispatch(contractCePointsSlice.actions.deleteContractCePointSuccess(contractCePointId));
        } catch (error) {
            dispatch(contractCePointsSlice.actions.hasError(true));
            handleError(error);
        }
    };
}
